import { Capacitor } from "@capacitor/core";

const ROUTING_API_BASE =
  Capacitor.getPlatform() === "android"
    ? "https://thera-rutas-ecuador.pages.dev"
    : "";
import { APPROACH_ROUTE_CACHE_STORE, openDeviceDatabase } from "./device-storage";
import { haversineKm, nearestRoutePosition, type RouteCoordinate } from "./navigation-core";

export type ApproachMode = "foot-walking" | "driving-car";

export type ApproachInstruction = {
  instruction: string;
  name: string;
  distanceM: number;
  durationSeconds: number;
  startIndex: number;
  endIndex: number;
};

export type RoadApproachRoute = {
  cacheKey: string;
  routeId: string;
  mode: ApproachMode;
  origin: { latitude: number; longitude: number };
  destination: { latitude: number; longitude: number };
  coordinates: number[][];
  distanceM: number;
  durationSeconds: number;
  instructions: ApproachInstruction[];
  fetchedAt: number;
  offline: boolean;
};

type RoutingResponse = {
  coordinates?: number[][];
  distanceM?: number;
  durationSeconds?: number;
  instructions?: ApproachInstruction[];
};

const cacheKey = (routeId: string, mode: ApproachMode) => `${routeId}:${mode}`;

async function saveApproachRoute(route: RoadApproachRoute) {
  if (!("indexedDB" in window)) return;
  const database = await openDeviceDatabase();
  await new Promise<void>((resolve, reject) => {
    const transaction = database.transaction(APPROACH_ROUTE_CACHE_STORE, "readwrite");
    transaction.objectStore(APPROACH_ROUTE_CACHE_STORE).put(route);
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
  database.close();
}

export async function loadStoredApproachRoute(routeId: string, mode: ApproachMode) {
  if (!("indexedDB" in window)) return null;
  const database = await openDeviceDatabase();
  const stored = await new Promise<RoadApproachRoute | null>((resolve, reject) => {
    const request = database.transaction(APPROACH_ROUTE_CACHE_STORE, "readonly")
      .objectStore(APPROACH_ROUTE_CACHE_STORE)
      .get(cacheKey(routeId, mode));
    request.onsuccess = () => resolve(request.result ?? null);
    request.onerror = () => reject(request.error);
  });
  database.close();
  return stored ? { ...stored, offline: true } : null;
}

function validCoordinates(value: unknown): value is number[][] {
  return Array.isArray(value)
    && value.length >= 2
    && value.length <= 100_000
    && value.every((coordinate) => Array.isArray(coordinate)
      && coordinate.length >= 2
      && Number.isFinite(coordinate[0])
      && Number.isFinite(coordinate[1]));
}

function storedRouteMatchesCurrentApproach(
  route: RoadApproachRoute,
  origin: { latitude: number; longitude: number },
  destination: { latitude: number; longitude: number },
) {
  if (haversineKm(route.destination, destination) > 0.15) return false;
  const points: RouteCoordinate[] = route.coordinates.map((coordinate) => ({
    longitude: Number(coordinate[0]),
    latitude: Number(coordinate[1]),
    elevation: null,
  }));
  const nearest = nearestRoutePosition(origin, points);
  return haversineKm(route.origin, origin) <= 1 || Boolean(nearest && nearest.distanceM <= 1_000);
}

export async function fetchRoadApproachRoute({
  routeId,
  mode,
  origin,
  destination,
}: {
  routeId: string;
  mode: ApproachMode;
  origin: { latitude: number; longitude: number };
  destination: { latitude: number; longitude: number };
}) {
  try {
    if (!navigator.onLine) throw new Error("OFFLINE");
    const response = await fetch(`${ROUTING_API_BASE}/api/routing/approach`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ mode, origin, destination }),
    });
    if (!response.ok) {
      const detail = await response.json().catch(() => null) as { error?: string; code?: string } | null;
      const error = new Error(detail?.error || "No fue posible calcular el camino al inicio.");
      error.name = detail?.code || "ROUTING_ERROR";
      throw error;
    }
    const payload = await response.json() as RoutingResponse;
    if (!validCoordinates(payload.coordinates)
      || !Number.isFinite(payload.distanceM)
      || !Number.isFinite(payload.durationSeconds)) {
      throw new Error("La respuesta de navegación no contiene un recorrido válido.");
    }
    const route: RoadApproachRoute = {
      cacheKey: cacheKey(routeId, mode),
      routeId,
      mode,
      origin,
      destination,
      coordinates: payload.coordinates,
      distanceM: Math.max(0, Number(payload.distanceM)),
      durationSeconds: Math.max(0, Number(payload.durationSeconds)),
      instructions: Array.isArray(payload.instructions) ? payload.instructions.slice(0, 250) : [],
      fetchedAt: Date.now(),
      offline: false,
    };
    await saveApproachRoute(route).catch(() => undefined);
    return route;
  } catch (error) {
    const stored = await loadStoredApproachRoute(routeId, mode).catch(() => null);
    if (stored && storedRouteMatchesCurrentApproach(stored, origin, destination)) return stored;
    if (!navigator.onLine) {
      throw new Error("Sin conexión no existe todavía un camino guardado desde esta zona. Se mantiene la guía directa.");
    }
    throw error;
  }
}

export function roadApproachProgress(
  route: RoadApproachRoute | null,
  current?: { latitude: number; longitude: number },
) {
  if (!route || !current) return null;
  const points: RouteCoordinate[] = route.coordinates.map((coordinate) => ({
    longitude: Number(coordinate[0]),
    latitude: Number(coordinate[1]),
    elevation: Number.isFinite(coordinate[2]) ? Number(coordinate[2]) : null,
  }));
  const nearest = nearestRoutePosition(current, points);
  if (!nearest) return null;
  const geometryKm = Math.max(points.slice(1).reduce(
    (total, point, index) => total + haversineKm(points[index], point),
    0,
  ), 0.001);
  const fractionCompleted = Math.min(1, Math.max(0, nearest.alongKm / geometryKm));
  const fractionRemaining = 1 - fractionCompleted;
  const remainingKm = Math.max(0, (route.distanceM / 1000) * fractionRemaining);
  const instruction = route.instructions.find((step) => step.endIndex >= nearest.segment)
    ?? route.instructions.at(-1)
    ?? null;
  return {
    remainingKm,
    remainingSeconds: Math.round(route.durationSeconds * fractionRemaining),
    deviationM: nearest.distanceM,
    instruction,
  };
}
